import { useState, useEffect, useMemo } from "react";
import { useAdminLanguage } from "../../layouts/AdminLayout";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "../../components/ui/table";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { DollarSign, Clock, Trash2, ArrowUpDown, ChevronUp, ChevronDown, Download, Save, Settings2 } from "lucide-react";
import { Button } from "../../components/ui/button";
import { toast } from "sonner";
import { exportToCSV } from "../../utils/exportCSV";

const API = process.env.REACT_APP_BACKEND_URL;

export default function AdminBilling() {
  const [data, setData] = useState({ transactions: [], total_revenue: 0 });
  const [loading, setLoading] = useState(true);
  const [sortKey, setSortKey] = useState("created_at");
  const [sortDir, setSortDir] = useState("desc");
  const { t } = useAdminLanguage();

  // Pricing state
  const [pricing, setPricing] = useState(null);
  const [savingPricing, setSavingPricing] = useState(false);

  useEffect(() => { fetchBilling(); fetchPricing(); }, []);

  const fetchBilling = async () => {
    try {
      const res = await fetch(`${API}/api/admin/billing`, { credentials: 'include' });
      if (res.ok) setData(await res.json());
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const fetchPricing = async () => {
    try {
      const res = await fetch(`${API}/api/admin/pricing`, { credentials: 'include' });
      if (res.ok) setPricing(await res.json());
    } catch (e) { console.error(e); }
  };

  const handleSavePricing = async () => {
    setSavingPricing(true);
    try {
      const res = await fetch(`${API}/api/admin/pricing`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(pricing),
        credentials: 'include'
      });
      if (res.ok) {
        toast.success("Pricing updated successfully");
      } else {
        toast.error("Failed to update pricing");
      }
    } catch (e) {
      toast.error("Connection error");
    } finally {
      setSavingPricing(false);
    }
  };

  const updatePricing = (pack, field, value) => {
    setPricing(prev => ({
      ...prev,
      [pack]: { ...prev[pack], [field]: value }
    }));
  };

  const handleSort = (key) => {
    if (sortKey === key) setSortDir(d => d === "asc" ? "desc" : "asc");
    else { setSortKey(key); setSortDir("asc"); }
  };

  const SortIcon = ({ col }) => {
    if (sortKey !== col) return <ArrowUpDown className="w-3 h-3 ml-1 opacity-30" />;
    return sortDir === "asc" ? <ChevronUp className="w-3 h-3 ml-1" /> : <ChevronDown className="w-3 h-3 ml-1" />;
  };

  const sorted = useMemo(() => {
    return [...data.transactions].sort((a, b) => {
      let va = a[sortKey] ?? "", vb = b[sortKey] ?? "";
      if (typeof va === "number") return sortDir === "asc" ? va - vb : vb - va;
      return sortDir === "asc" ? String(va).localeCompare(String(vb)) : String(vb).localeCompare(String(va));
    });
  }, [data.transactions, sortKey, sortDir]);

  const handleDelete = async (txId) => {
    if (!window.confirm(t("confirm_delete"))) return;
    try {
      const res = await fetch(`${API}/api/admin/billing/${txId}`, { method: "DELETE", credentials: "include" });
      if (res.ok) { toast.success(t("transaction_deleted")); fetchBilling(); }
    } catch (e) { toast.error(t("connection_error")); }
  };

  const statusColor = (status) => {
    switch (status) {
      case "completed": return "bg-emerald-500/10 text-emerald-400 border-emerald-500/20";
      case "pending": return "bg-yellow-500/10 text-yellow-400 border-yellow-500/20";
      default: return "bg-zinc-700/10 text-zinc-400 border-zinc-700/20";
    }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold text-white mb-1">{t("billing")}</h1>
          <p className="text-zinc-500 text-sm">Manage pricing and transactions.</p>
        </div>
        <div className="flex items-center gap-3">
          <Button variant="outline" className="border-zinc-700 text-zinc-400 hover:text-white" data-testid="export-billing-csv" onClick={() => exportToCSV(sorted, [
            { label: "ID Transaction", accessor: r => r.transaction_id || r.session_id || "" },
            { label: "Client", key: "user_name" },
            { label: "Email", key: "user_email" },
            { label: "Pack", accessor: r => r.package_name || r.package_id || "" },
            { label: "Montant ($)", key: "amount" },
            { label: "Statut", key: "status" },
            { label: "Date", key: "created_at" }
          ], "ema_facturation")}>
            <Download className="w-4 h-4 mr-2" />CSV
          </Button>
          <div className="bg-emerald-500/10 border border-emerald-500/20 px-4 py-2 rounded-lg text-emerald-400 font-bold flex items-center gap-2">
            <DollarSign className="w-5 h-5" />
            {t("total_revenue")}: ${data.total_revenue?.toFixed(2) || "0.00"}
          </div>
        </div>
      </div>

      {/* Pricing Configuration */}
      {pricing && (
        <Card className="bg-zinc-900 border-zinc-800 mb-6" data-testid="pricing-config-card">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-white text-lg">
              <Settings2 className="w-5 h-5 text-amber-400" />
              Pricing Configuration
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {/* Custom Pack */}
              <div className="bg-zinc-800/60 rounded-xl p-5 border border-zinc-700/50">
                <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-blue-400" />
                  Custom Pack
                </h3>
                <div className="space-y-3">
                  <div>
                    <Label className="text-zinc-400 text-xs">Price per interview ($)</Label>
                    <Input
                      data-testid="pricing-custom-price"
                      type="number"
                      step="0.01"
                      value={pricing.custom_pack?.price_per_interview || 0}
                      onChange={(e) => updatePricing('custom_pack', 'price_per_interview', parseFloat(e.target.value) || 0)}
                      className="bg-zinc-900 border-zinc-700 text-white mt-1 h-9"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <Label className="text-zinc-400 text-xs">Min qty</Label>
                      <Input
                        type="number"
                        value={pricing.custom_pack?.min_quantity || 1}
                        onChange={(e) => updatePricing('custom_pack', 'min_quantity', parseInt(e.target.value) || 1)}
                        className="bg-zinc-900 border-zinc-700 text-white mt-1 h-9"
                      />
                    </div>
                    <div>
                      <Label className="text-zinc-400 text-xs">Max qty</Label>
                      <Input
                        type="number"
                        value={pricing.custom_pack?.max_quantity || 49}
                        onChange={(e) => updatePricing('custom_pack', 'max_quantity', parseInt(e.target.value) || 49)}
                        className="bg-zinc-900 border-zinc-700 text-white mt-1 h-9"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Pack 50 */}
              <div className="bg-zinc-800/60 rounded-xl p-5 border border-zinc-700/50">
                <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-emerald-400" />
                  {pricing.pack_50?.name || "Pack 50"}
                </h3>
                <div className="space-y-3">
                  <div>
                    <Label className="text-zinc-400 text-xs">Pack name</Label>
                    <Input
                      data-testid="pricing-pack50-name"
                      value={pricing.pack_50?.name || "Pack 50"}
                      onChange={(e) => updatePricing('pack_50', 'name', e.target.value)}
                      className="bg-zinc-900 border-zinc-700 text-white mt-1 h-9"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <Label className="text-zinc-400 text-xs">Price ($)</Label>
                      <Input
                        data-testid="pricing-pack50-price"
                        type="number"
                        step="0.01"
                        value={pricing.pack_50?.price || 0}
                        onChange={(e) => updatePricing('pack_50', 'price', parseFloat(e.target.value) || 0)}
                        className="bg-zinc-900 border-zinc-700 text-white mt-1 h-9"
                      />
                    </div>
                    <div>
                      <Label className="text-zinc-400 text-xs">Credits</Label>
                      <Input
                        data-testid="pricing-pack50-credits"
                        type="number"
                        value={pricing.pack_50?.credits || 0}
                        onChange={(e) => updatePricing('pack_50', 'credits', parseInt(e.target.value) || 0)}
                        className="bg-zinc-900 border-zinc-700 text-white mt-1 h-9"
                      />
                    </div>
                  </div>
                  <p className="text-zinc-500 text-xs">
                    = ${pricing.pack_50?.credits ? ((pricing.pack_50?.price || 0) / pricing.pack_50.credits).toFixed(2) : '0'} / interview
                  </p>
                </div>
              </div>

              {/* Pack 120 */}
              <div className="bg-zinc-800/60 rounded-xl p-5 border border-zinc-700/50">
                <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-purple-400" />
                  {pricing.pack_120?.name || "Pack 120"}
                </h3>
                <div className="space-y-3">
                  <div>
                    <Label className="text-zinc-400 text-xs">Pack name</Label>
                    <Input
                      data-testid="pricing-pack120-name"
                      value={pricing.pack_120?.name || "Pack 120"}
                      onChange={(e) => updatePricing('pack_120', 'name', e.target.value)}
                      className="bg-zinc-900 border-zinc-700 text-white mt-1 h-9"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <Label className="text-zinc-400 text-xs">Price ($)</Label>
                      <Input
                        data-testid="pricing-pack120-price"
                        type="number"
                        step="0.01"
                        value={pricing.pack_120?.price || 0}
                        onChange={(e) => updatePricing('pack_120', 'price', parseFloat(e.target.value) || 0)}
                        className="bg-zinc-900 border-zinc-700 text-white mt-1 h-9"
                      />
                    </div>
                    <div>
                      <Label className="text-zinc-400 text-xs">Credits</Label>
                      <Input
                        data-testid="pricing-pack120-credits"
                        type="number"
                        value={pricing.pack_120?.credits || 0}
                        onChange={(e) => updatePricing('pack_120', 'credits', parseInt(e.target.value) || 0)}
                        className="bg-zinc-900 border-zinc-700 text-white mt-1 h-9"
                      />
                    </div>
                  </div>
                  <p className="text-zinc-500 text-xs">
                    = ${pricing.pack_120?.credits ? ((pricing.pack_120?.price || 0) / pricing.pack_120.credits).toFixed(2) : '0'} / interview
                  </p>
                </div>
              </div>
            </div>

            <div className="flex justify-end mt-5">
              <Button
                data-testid="save-pricing-btn"
                onClick={handleSavePricing}
                disabled={savingPricing}
                className="bg-amber-500 hover:bg-amber-600 text-black font-medium px-6"
              >
                <Save className="w-4 h-4 mr-2" />
                {savingPricing ? "Saving..." : "Save Pricing"}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Transactions Table */}
      <Card className="bg-zinc-900 border-zinc-800">
        <CardContent className="p-0 overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="border-zinc-800">
                <TableHead className="text-zinc-400 cursor-pointer select-none" onClick={() => handleSort("transaction_id")}>
                  <span className="flex items-center">ID<SortIcon col="transaction_id" /></span>
                </TableHead>
                <TableHead className="text-zinc-400 cursor-pointer select-none" onClick={() => handleSort("user_name")}>
                  <span className="flex items-center">Client<SortIcon col="user_name" /></span>
                </TableHead>
                <TableHead className="text-zinc-400 cursor-pointer select-none" onClick={() => handleSort("package_name")}>
                  <span className="flex items-center">{t("package")}<SortIcon col="package_name" /></span>
                </TableHead>
                <TableHead className="text-zinc-400 cursor-pointer select-none" onClick={() => handleSort("amount")}>
                  <span className="flex items-center">{t("amount")}<SortIcon col="amount" /></span>
                </TableHead>
                <TableHead className="text-zinc-400 cursor-pointer select-none" onClick={() => handleSort("status")}>
                  <span className="flex items-center">{t("status")}<SortIcon col="status" /></span>
                </TableHead>
                <TableHead className="text-zinc-400 cursor-pointer select-none" onClick={() => handleSort("created_at")}>
                  <span className="flex items-center">{t("date")}<SortIcon col="created_at" /></span>
                </TableHead>
                <TableHead className="text-right text-zinc-400">{t("actions")}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {sorted.map(tx => (
                <TableRow key={tx.transaction_id || tx.session_id} className="border-zinc-800 hover:bg-zinc-800/50">
                  <TableCell className="font-mono text-zinc-500 text-xs">{(tx.transaction_id || tx.session_id || "").slice(0, 12)}</TableCell>
                  <TableCell>
                    <div>
                      <span className="text-white text-sm">{tx.user_name || "N/A"}</span>
                      <p className="text-zinc-500 text-xs">{tx.user_email || ""}</p>
                    </div>
                  </TableCell>
                  <TableCell className="text-zinc-300 text-sm">{tx.package_name || tx.package_id || "-"}</TableCell>
                  <TableCell className="text-white font-medium">${(tx.amount || 0).toFixed(2)}</TableCell>
                  <TableCell>
                    <Badge className={statusColor(tx.status)}>{tx.status?.toUpperCase() || "N/A"}</Badge>
                  </TableCell>
                  <TableCell className="text-zinc-400 text-xs">
                    <div className="flex items-center gap-1.5"><Clock className="w-3 h-3" />{tx.created_at ? new Date(tx.created_at).toLocaleString() : "-"}</div>
                  </TableCell>
                  <TableCell className="text-right">
                    <Button variant="ghost" size="icon" className="h-7 w-7 text-zinc-400 hover:text-red-400" onClick={() => handleDelete(tx.transaction_id)} title={t("delete")}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
              {sorted.length === 0 && (
                <TableRow><TableCell colSpan={7} className="text-center text-zinc-500 py-8">{t("no_data")}</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
